import React, { useEffect, useRef, useState } from 'react';
import { notification } from 'antd';

const TextEditor = ({ value = '', onChange, variables = {}, placeholder }) => {
  const inputRef = useRef(null);
  const [internalValue, setInternalValue] = useState(value);
  const [showVariableList, setShowVariableList] = useState(false);
  const [listPosition, setListPosition] = useState({ top: 0, left: 0 });

  // Add effect to watch for variables changes
  useEffect(() => {
    checkAndUpdateVariables();
  }, [variables]);

  useEffect(() => {
    if (inputRef.current && value !== inputRef.current.textContent) {
      inputRef.current.textContent = value;
    }
  }, [value]);

  const checkAndUpdateVariables = () => {
    if (!inputRef.current) return;

    const text = inputRef.current.textContent;
    const regex = /{{([^}]+)}}/g;
    let match;
    let hasChanges = false;
    let updatedText = text;
    const sel = window.getSelection();
    const cursorPosition = getGlobalCursorPosition();

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, varName] = match;
      if (!Object.keys(variables).includes(varName)) {
        const startPos = match.index;
        const endPos = startPos + fullMatch.length;

        // Only remove if it's not currently being edited
        if (cursorPosition < startPos || cursorPosition > endPos) {
          updatedText = updatedText.replace(fullMatch, '');
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      // Preserve cursor position
      const range = document.createRange();
      range.setStart(inputRef.current.firstChild || inputRef.current, cursorPosition);
      range.collapse(true);

      inputRef.current.textContent = updatedText;
      setInternalValue(updatedText);
      onChange?.(updatedText);

      // Restore cursor position
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const getGlobalCursorPosition = () => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return 0;
    const range = sel.getRangeAt(0);
    const preRange = document.createRange();
    preRange.selectNodeContents(inputRef.current);
    preRange.setEnd(range.startContainer, range.startOffset);
    return preRange.toString().length;
  };

  const handleInput = () => {
    const text = inputRef.current.textContent;
    setInternalValue(text);
    onChange?.(text);

    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    const cursorPosition = getGlobalCursorPosition();

    // Check if '{{' is at cursor position
    const beforeCursor = text.slice(0, cursorPosition);
    const isTypingVariable = beforeCursor.endsWith('{{');

    if (isTypingVariable) {
      const { top, left } = range.getBoundingClientRect();
      setListPosition({ top: top + 25, left: left - 10 });
      setShowVariableList(true);
    } else {
      setShowVariableList(false);
    }
  };

  const insertVariable = variable => {
    // Check if variable is available
    if (!Object.keys(variables).includes(variable)) {
      notification.warning({
        message: 'Variable not available',
        description: 'This variable is no longer available.',
      });
      return;
    }

    const sel = window.getSelection();
    const range = sel.getRangeAt(0);
    range.setStart(range.startContainer, range.startOffset - 2);
    range.deleteContents();

    const textNode = document.createTextNode(`{{${variable}}}`);
    range.insertNode(textNode);
    inputRef.current.normalize();

    const newRange = document.createRange();
    newRange.setStart(textNode, textNode.length);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    setInternalValue(inputRef.current.textContent);
    setShowVariableList(false);
    onChange?.(inputRef.current.textContent);
  };

  const handleKeyDown = e => {
    if (e.key === 'Backspace') {
      const globalCursorPos = getGlobalCursorPosition();
      const text = inputRef.current.textContent;

      if (text[globalCursorPos - 1] === '}' && text[globalCursorPos - 2] === '}') {
        const searchText = text.substring(0, globalCursorPos);
        const openIndex = searchText.lastIndexOf('{{');

        if (openIndex !== -1) {
          e.preventDefault();

          const node = inputRef.current.firstChild;
          const variableRange = document.createRange();
          variableRange.setStart(node, openIndex);
          variableRange.setEnd(node, globalCursorPos);
          variableRange.deleteContents();

          const newText = inputRef.current.textContent;
          setInternalValue(newText);
          onChange?.(newText);

          const newRange = document.createRange();
          newRange.setStart(node, openIndex);
          newRange.collapse(true);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      }
    }
  };

  return (
    <div className='relative'>
      <div className='mb-4 relative'>
        <div
          className='w-full min-h-[100px] p-3 border rounded-lg resize-y overflow-auto 
                   focus:outline-none focus:ring-2 focus:ring-blue-300 prose'
          contentEditable
          ref={inputRef}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          suppressContentEditableWarning
        />
        {!internalValue && (
          <span className='absolute left-4 top-4 text-gray-400 pointer-events-none'>{placeholder}</span>
        )}
      </div>

      {showVariableList && (
        <div
          className='fixed bg-white border border-gray-100 shadow-lg rounded-md py-1'
          style={{
            top: listPosition.top,
            left: listPosition.left,
            zIndex: 1000,
            minWidth: '160px',
          }}
        >
          {Object.keys(variables).map(varName => (
            <button
              key={varName}
              onClick={() => insertVariable(varName)}
              className='w-full px-3 py-1.5 text-sm text-left text-gray-700 
                       hover:bg-blue-50 hover:text-blue-600 transition-colors'
            >
              {varName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TextEditor;
